import {Component, Input, Optional} from '@angular/core';
import {Ast} from '@ngmedax/expression-compiler';
import {TranslationService} from '@ngmedax/translation';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {DependencySource, DependencySummaryPart, dependencySource, dependencySummaryParts} from './dependency-conditions';

/** Renders compact property previews and detailed dialog previews using the same condition wording. */
@Component({
  selector: 'app-qa-dependency-summary',
  templateUrl: './dependency-summary.component.html',
  styleUrls: ['./dependency-summary.component.css']
})
export class DependencySummaryComponent {
  @Input() group: Ast.Group;
  @Input() sources: DependencySource[] = [];
  @Input() compact = false;

  /** Uses administration translations independently of the questionnaire content language. */
  public constructor(@Optional() private translation: TranslationService) {}

  /** Keeps chip elements and their hover state stable during draft change detection. */
  public trackPart(index: number): number { return index; }

  /** Resolves source metadata without altering the original expression. */
  public source(rule: Ast.Expression): DependencySource { return dependencySource(rule.variable, this.sources); }

  /** Translates administration labels independently of questionnaire content. */
  public translate(text: string): string {
    return this.translation ? this.translation.translate({text, scope: TRANSLATION_EDITOR_SCOPE}) : text;
  }

  /** Reuses the existing comparison wording and its distinction between missing and false values. */
  public comparison(rule: Ast.Expression): string {
    return dependencySummaryParts({type: 'group', operator: '&&', elements: [rule]}, this.sources,
      text => this.translate(text)).filter(part => part.kind === 'text').map(part => part.text).join('').trim();
  }

  /** Keeps the complete condition available to assistive technology. */
  public get summary(): string { return this.parts.map(part => part.text).join(''); }

  /** Reflects nested draft edits even when the outer group reference stays unchanged. */
  public get parts(): DependencySummaryPart[] {
    return dependencySummaryParts(this.group, this.sources, text => this.translate(text));
  }
}
